/**
 * Audio pre-processing (implementation-plan.md §1.3b) — the layer between the mic and
 * `callout-transcribe`.
 *
 *   capture → high-pass 80 Hz → normalise to −3 dBFS → 16 kHz mono WAV → chunk → upload
 *
 * Everything in the first half of this file is pure DSP (Float32Array in, Float32Array /
 * Uint8Array out) so it is unit-testable outside a browser; the browser-only steps
 * (decode, resample) sit at the bottom and are exercised by the capture E2E.
 *
 * 16 kHz mono 16-bit PCM is ~3× smaller than the raw Opus/AAC the recorder hands us, and it
 * is what gpt-4o-mini-transcribe wants.
 */

/** Transcription contract: 16 kHz mono. */
export const TARGET_SAMPLE_RATE = 16_000;
/** Wind rumble sits below this; speech does not. */
export const HIGH_PASS_HZ = 80;
/** Normalisation target (leaves headroom for the encoder). */
export const TARGET_PEAK_DBFS = -3;
/** Below this, "normalising" would only amplify the noise floor. */
export const MIN_NORMALISE_DBFS = -60;

/** gpt-4o-mini-transcribe per-request limits (plan §4 / rendering intel). */
export const CHUNK_MAX_BYTES = 25 * 1024 * 1024;
export const CHUNK_MAX_DURATION_MS = 25 * 60 * 1000;

export interface BiquadCoeffs {
  b0: number;
  b1: number;
  b2: number;
  a1: number;
  a2: number;
}

export interface ChunkSpec {
  chunkIndex: number;
  startByte: number;
  endByte: number;
  /** Wall-clock offset of the chunk start within the recording, in ms. */
  startWallOffsetMs: number;
  durationMs: number;
}

/** Bytes per millisecond of the 16-bit mono payload at `sampleRate`. */
export function bytesPerMs(sampleRate = TARGET_SAMPLE_RATE): number {
  return (sampleRate * 2) / 1000;
}

/**
 * The WebM init segment of a MediaRecorder stream: everything before the first Cluster element
 * (ID 0x1F43B675). A recorder timeslice after the first one contains clusters only, so
 * `decodeAudioData` cannot read it standalone — prepending this makes each chunk decodable and
 * is what keeps the live transcript live during an open-mic session.
 *
 * Returns null when no Cluster element is present (then the caller falls back to decoding the
 * segment in one piece at segment end).
 */
export function webmInitSegment(bytes: Uint8Array): Uint8Array<ArrayBuffer> | null {
  const cluster = [0x1f, 0x43, 0xb6, 0x75];
  for (let i = 0; i + cluster.length <= bytes.length; i += 1) {
    if (cluster.every((byte, offset) => bytes[i + offset] === byte)) {
      const header = new Uint8Array(i);
      header.set(bytes.subarray(0, i));
      return header;
    }
  }
  return null;
}

/** True when an error came from the audio decoder (used to pick the one-piece fallback). */
export function isDecodeFailure(error: unknown): boolean {
  const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
  return /decode|EncodingError|NotSupportedError|AudioBuffer|unable to/i.test(message);
}

/** RBJ audio-EQ-cookbook high-pass (Q = 1/√2, Butterworth). */
export function highPassCoefficients(sampleRate: number, cutoffHz = HIGH_PASS_HZ): BiquadCoeffs {
  const w0 = (2 * Math.PI * cutoffHz) / sampleRate;
  const cosW0 = Math.cos(w0);
  const alpha = Math.sin(w0) / (2 * Math.SQRT1_2);
  const a0 = 1 + alpha;
  return {
    b0: ((1 + cosW0) / 2) / a0,
    b1: (-(1 + cosW0)) / a0,
    b2: ((1 + cosW0) / 2) / a0,
    a1: (-2 * cosW0) / a0,
    a2: (1 - alpha) / a0,
  };
}

/** Non-finite samples (dropped frames) become silence before they poison the chain. */
function sanitise(samples: Float32Array): Float32Array {
  const out = new Float32Array(samples.length);
  for (let i = 0; i < samples.length; i += 1) {
    const s = samples[i]!;
    out[i] = Number.isFinite(s) ? s : 0;
  }
  return out;
}

/** Direct-form-I biquad with zero initial state. */
export function applyBiquad(samples: Float32Array, coeffs: BiquadCoeffs): Float32Array {
  const clean = sanitise(samples);
  const out = new Float32Array(clean.length);
  let x1 = 0;
  let x2 = 0;
  let y1 = 0;
  let y2 = 0;
  for (let i = 0; i < clean.length; i += 1) {
    const x0 = clean[i]!;
    const y0 = coeffs.b0 * x0 + coeffs.b1 * x1 + coeffs.b2 * x2 - coeffs.a1 * y1 - coeffs.a2 * y2;
    out[i] = Number.isFinite(y0) ? y0 : 0;
    x2 = x1;
    x1 = x0;
    y2 = y1;
    y1 = out[i]!;
  }
  return out;
}

/** 80 Hz high-pass — plan §1.3b step 2, first bullet. */
export function applyHighPass(
  samples: Float32Array,
  sampleRate = TARGET_SAMPLE_RATE,
  cutoffHz = HIGH_PASS_HZ,
): Float32Array {
  return applyBiquad(samples, highPassCoefficients(sampleRate, cutoffHz));
}

/** Peak level in dBFS; silence is -Infinity (never NaN). */
export function peakDbfs(samples: Float32Array): number {
  let peak = 0;
  for (let i = 0; i < samples.length; i += 1) {
    const s = Math.abs(samples[i]!);
    if (Number.isFinite(s) && s > peak) peak = s;
  }
  if (peak <= 0) return -Infinity;
  return 20 * Math.log10(peak);
}

/**
 * Scales the buffer so its peak sits at `targetPeakDb` dBFS. Silence and a near-silent
 * noise floor are returned unchanged (amplifying them would only manufacture noise).
 */
export function normalizeToPeak(samples: Float32Array, targetPeakDb = TARGET_PEAK_DBFS): Float32Array {
  const clean = sanitise(samples);
  const peak = peakAbs(clean);
  if (peak <= 0) return clean;
  if (20 * Math.log10(peak) < MIN_NORMALISE_DBFS) return clean;

  const gain = 10 ** (targetPeakDb / 20) / peak;
  if (!Number.isFinite(gain) || gain <= 0) return clean;

  const out = new Float32Array(clean.length);
  for (let i = 0; i < clean.length; i += 1) {
    const v = clean[i]! * gain;
    out[i] = Number.isFinite(v) ? Math.max(-1, Math.min(1, v)) : 0;
  }
  return out;
}

function peakAbs(samples: Float32Array): number {
  let peak = 0;
  for (let i = 0; i < samples.length; i += 1) {
    const s = Math.abs(samples[i]!);
    if (Number.isFinite(s) && s > peak) peak = s;
  }
  return peak;
}

/** Float32 [-1,1] → signed 16-bit PCM, saturating (never wrapping). */
export function floatToPcm16(samples: Float32Array): Int16Array {
  const out = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i += 1) {
    const s = samples[i]!;
    if (!Number.isFinite(s)) {
      out[i] = 0;
      continue;
    }
    const clamped = Math.max(-1, Math.min(1, s));
    out[i] = Math.round(clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff);
  }
  return out;
}

/** Minimal 44-byte RIFF/WAVE header + PCM payload (mono, 16-bit). */
export function encodeWav(samples: Float32Array, sampleRate = TARGET_SAMPLE_RATE): Uint8Array<ArrayBuffer> {
  const pcm = floatToPcm16(samples);
  const dataBytes = pcm.length * 2;
  const buffer = new ArrayBuffer(44 + dataBytes);
  const view = new DataView(buffer);
  const ascii = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i += 1) view.setUint8(offset + i, text.charCodeAt(i));
  };

  ascii(0, 'RIFF');
  view.setUint32(4, 36 + dataBytes, true);
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  view.setUint32(16, 16, true); // PCM fmt-chunk size
  view.setUint16(20, 1, true); // format = PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate = rate × channels × bytes/sample
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  ascii(36, 'data');
  view.setUint32(40, dataBytes, true);

  new Int16Array(buffer, 44).set(pcm);
  return new Uint8Array(buffer);
}

/**
 * Splits a recording into upload-sized chunks that respect BOTH API limits, with no gaps and
 * no empty chunks. Sizes are spread evenly so every chunk carries the same amount of audio.
 */
export function chunkPlan({
  totalBytes,
  totalDurationMs,
  maxBytes = CHUNK_MAX_BYTES,
  maxDurationMs = CHUNK_MAX_DURATION_MS,
}: {
  totalBytes: number;
  totalDurationMs: number;
  sampleRate?: number;
  maxBytes?: number;
  maxDurationMs?: number;
}): ChunkSpec[] {
  if (totalBytes <= 0) return [];
  const byBytes = Math.ceil(totalBytes / maxBytes);
  const byDuration = totalDurationMs > 0 ? Math.ceil(totalDurationMs / maxDurationMs) : 1;
  const count = Math.max(1, byBytes, byDuration);

  const chunks: ChunkSpec[] = [];
  for (let i = 0; i < count; i += 1) {
    const startByte = Math.floor((i * totalBytes) / count);
    const endByte = Math.floor(((i + 1) * totalBytes) / count);
    if (endByte <= startByte) continue;
    chunks.push({
      chunkIndex: chunks.length,
      startByte,
      endByte,
      startWallOffsetMs: totalDurationMs > 0 ? (startByte / totalBytes) * totalDurationMs : 0,
      durationMs: totalDurationMs > 0 ? ((endByte - startByte) / totalBytes) * totalDurationMs : 0,
    });
  }
  return chunks;
}

// ---------------------------------------------------------------------------
// Browser-only steps (verified by the capture E2E, not by the unit suite)
// ---------------------------------------------------------------------------

export interface PreprocessResult {
  wav: Uint8Array<ArrayBuffer>;
  sampleRate: number;
  durationMs: number;
  peakDbfs: number;
  /** RMS of the raw capture, so the UI can say "holding the phone closer". */
  inputPeakDbfs: number;
}

/**
 * Full chain for one captured blob: decode → 80 Hz high-pass → −3 dBFS → 16 kHz mono WAV.
 * Uses OfflineAudioContext for the resample (the browser's own high-quality resampler).
 */
export async function preprocessRecording(blob: Blob): Promise<PreprocessResult> {
  const Ctx: typeof OfflineAudioContext =
    (globalThis as { OfflineAudioContext?: typeof OfflineAudioContext }).OfflineAudioContext ??
    (globalThis as unknown as { webkitOfflineAudioContext: typeof OfflineAudioContext })
      .webkitOfflineAudioContext;
  const AudioCtor: typeof AudioContext =
    (globalThis as { AudioContext?: typeof AudioContext }).AudioContext ??
    (globalThis as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;

  const decodeCtx = new AudioCtor();
  let decoded: AudioBuffer;
  try {
    decoded = await decodeCtx.decodeAudioData(await blob.arrayBuffer());
  } finally {
    void decodeCtx.close();
  }

  // Mono mixdown of whatever the device gave us.
  const frames = decoded.length;
  const mono = new Float32Array(frames);
  for (let c = 0; c < decoded.numberOfChannels; c += 1) {
    const data = decoded.getChannelData(c);
    for (let i = 0; i < frames; i += 1) mono[i] += data[i]! / decoded.numberOfChannels;
  }

  const filtered = applyHighPass(mono, decoded.sampleRate);
  const normalised = normalizeToPeak(filtered);
  const outFrames = Math.max(1, Math.ceil((frames / decoded.sampleRate) * TARGET_SAMPLE_RATE));

  const offline = new Ctx(1, outFrames, TARGET_SAMPLE_RATE);
  const src = offline.createBufferSource();
  const buf = offline.createBuffer(1, normalised.length, decoded.sampleRate);
  buf.getChannelData(0).set(normalised);
  src.buffer = buf;
  src.connect(offline.destination);
  src.start();
  const rendered = await offline.startRendering();
  const resampled = rendered.getChannelData(0);

  return {
    wav: encodeWav(resampled, TARGET_SAMPLE_RATE),
    sampleRate: TARGET_SAMPLE_RATE,
    durationMs: (resampled.length / TARGET_SAMPLE_RATE) * 1000,
    peakDbfs: peakDbfs(resampled),
    inputPeakDbfs: peakDbfs(mono),
  };
}
